import img from "../assests/images/white-logo.png";
import dubaiSkyline from "../assests/images/dubai-skyline.webp";

import { Link } from "react-router-dom";

import {
  EnvelopeIcon,
  MapPinIcon,
  PhoneIcon,
} from "@heroicons/react/24/outline";

export default function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="relative overflow-hidden bg-[#032B49] text-white">

      {/* =====================================================
          BACKGROUND
      ====================================================== */}

      {/* Main gradient */}
      <div
        className="
          absolute
          inset-0
          bg-gradient-to-r
          from-[#032B49]
          via-[#043A5D]
          to-[#062F4F]
        "
      />

      {/* Dubai skyline on right */}
      <div
        className="
          absolute
          inset-y-0
          right-0
          hidden
          w-[48%]
          bg-cover
          bg-center
          lg:block
        "
        style={{
          backgroundImage: `url(${dubaiSkyline})`,
        }}
      />

      {/* Dark overlay over skyline */}
      <div
        className="
          absolute
          inset-y-0
          right-0
          hidden
          w-[55%]
          bg-gradient-to-r
          from-[#032B49]
          via-[#032B49]/80
          to-[#032B49]/25
          lg:block
        "
      />

      {/* Overall dark overlay */}
      <div className="absolute inset-0 bg-[#032B49]/35" />


      {/* =====================================================
          DECORATIVE TOP CIRCLE
      ====================================================== */}

      <div
        className="
          pointer-events-none
          absolute
          -left-40
          -top-48
          h-[500px]
          w-[500px]
          rounded-full
          border
          border-[#0B6E8E]/30
        "
      />

      <div
        className="
          pointer-events-none
          absolute
          -left-32
          -top-40
          h-[380px]
          w-[380px]
          rounded-full
          border
          border-[#F7B928]/25
        "
      />


      {/* =====================================================
          DECORATIVE BOTTOM WAVES
      ====================================================== */}

      <div
        className="
          pointer-events-none
          absolute
          -bottom-24
          -left-20
          h-32
          w-[650px]
          rotate-[-5deg]
          rounded-[50%]
          border-t-[5px]
          border-[#0B6E8E]/50
        "
      />

      <div
        className="
          pointer-events-none
          absolute
          -bottom-20
          -left-32
          h-24
          w-[500px]
          rotate-[-5deg]
          rounded-[50%]
          border-t-[4px]
          border-[#F7B928]/90
        "
      />

      <div
        className="
          pointer-events-none
          absolute
          -bottom-28
          left-20
          h-28
          w-[650px]
          rotate-[3deg]
          rounded-[50%]
          border-t
          border-[#0B6E8E]/30
        "
      />


      {/* =====================================================
          GOLD TOP LINE
      ====================================================== */}

      <div className="relative z-10 h-[2px] w-full bg-[#F7B928]" />


      {/* =====================================================
          CONTENT
      ====================================================== */}

      <div className="relative z-10 mx-auto  max-w-[1536px]
              px-4
            sm:px-6
            lg:px-8">

        <div
          className="
            grid
            gap-8
            py-9
            sm:py-10
            md:grid-cols-2
            lg:grid-cols-12
            lg:gap-0
            lg:py-10
          "
        >

          {/* =================================================
              BRAND
          ================================================== */}

          <div
            className="
              md:col-span-2
              lg:col-span-4
              lg:pr-10
            "
          >

            {/* LOGO */}
            <Link
              to="/"
              className="inline-flex items-center"
            >
              <img
                src={img}
                alt="Cheap Dubai Hotels"
                className="
                  block
                  h-auto
                  w-[145px]
                  object-contain
                  sm:w-[165px]
                "
              />
            </Link>


            {/* ABOUT */}
            <p
              className="
                mt-3
                max-w-[380px]
                text-xs
                leading-5
                text-white/65
              "
            >
              Discover the best hotels in Dubai, compare rates,
              and find the perfect stay at the right price.
            </p>


            {/* GOLD LINE */}
            <div className="mt-4 h-[2px] w-24 bg-[#F7B928]" />




          </div>


          {/* =================================================
              QUICK LINKS
          ================================================== */}

          <div
            className="
              lg:col-span-2
              lg:border-l
              lg:border-white/15
              lg:px-7
            "
          >

            <FooterTitle title="Quick Links" />

            <div className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2">

              <FooterLink to="/">
                Home
              </FooterLink>

              <FooterLink to="/about-us">
                About Us
              </FooterLink>

              <FooterLink to="/hotels">
                Hotels
              </FooterLink>

              <FooterLink to="/blog">
                Blog
              </FooterLink>

              <FooterLink to="/services">
                Dubai Guides
              </FooterLink>

              <FooterLink to="/contact-us">
                Contact
              </FooterLink>

            </div>

          </div>


          {/* =================================================
              CONTACT
          ================================================== */}

          <div
            className="
              lg:col-span-3
              lg:border-l
              lg:border-white/15
              lg:px-7
            "
          >

            <FooterTitle title="Contact Us" />

            <div className="mt-4 space-y-3">

              {/* EMAIL */}
              <a
                href="mailto:info@southtravels.com"
                className="
                  group
                  flex
                  items-center
                  gap-3
                  text-xs
                  text-white/70
                  transition-colors
                  hover:text-white
                "
              >

                <span
                  className="
                    flex
                    h-9
                    w-9
                    shrink-0
                    items-center
                    justify-center
                    rounded-full
                    border
                    border-[#0B6E8E]
                    bg-[#0B6E8E]/30
                    text-[#F7B928]
                  "
                >
                  <EnvelopeIcon className="h-4 w-4" />
                </span>

                <span>
                  info@southtravels.com
                </span>

              </a>


              {/* PHONE */}
              <a
                href="tel:+971502477593"
                className="
                  group
                  flex
                  items-center
                  gap-3
                  text-xs
                  text-white/70
                  transition-colors
                  hover:text-white
                "
              >

                <span
                  className="
                    flex
                    h-9
                    w-9
                    shrink-0
                    items-center
                    justify-center
                    rounded-full
                    border
                    border-[#0B6E8E]
                    bg-[#0B6E8E]/30
                    text-[#F7B928]
                  "
                >
                  <PhoneIcon className="h-4 w-4" />
                </span>

                <span>
                  +971 50 247 7593
                </span>

              </a>


              {/* LOCATION */}
              <div
                className="
                  flex
                  items-center
                  gap-3
                  text-xs
                  text-white/70
                "
              >

                <span
                  className="
                    flex
                    h-9
                    w-9
                    shrink-0
                    items-center
                    justify-center
                    rounded-full
                    border
                    border-[#0B6E8E]
                    bg-[#0B6E8E]/30
                    text-[#F7B928]
                  "
                >
                  <MapPinIcon className="h-4 w-4" />
                </span>

                <span>
                  Office 302, 3rd Flr. HDS Tower Cluster F, Jumeirah Lakes Towers Dubai
                </span>

              </div>

            </div>

          </div>


          {/* =================================================
              FOLLOW US
          ================================================== */}

          <div
            className="
              lg:col-span-3
              lg:border-l
              lg:border-white/15
              lg:px-7
            "
          >

            <FooterTitle title="Follow Us" />

            <div className="mt-4 flex items-center gap-2">

              <SocialIcon
                href="https://www.facebook.com/SouthTravelsUAE/"
                label="Facebook"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="h-4 w-4"
                >
                  <path d="M14 8h3V4h-3c-3.31 0-5 1.69-5 5v3H6v4h3v8h4v-8h3l1-4h-4V9c0-.69.31-1 1-1Z" />
                </svg>
              </SocialIcon>


              <SocialIcon
                href="https://www.instagram.com/southtravels_dmcc/"
                label="Instagram"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  className="h-4 w-4"
                >
                  <rect
                    x="3"
                    y="3"
                    width="18"
                    height="18"
                    rx="5"
                  />

                  <circle
                    cx="12"
                    cy="12"
                    r="4"
                  />

                  <circle
                    cx="17.5"
                    cy="6.5"
                    r="1"
                    fill="currentColor"
                    stroke="none"
                  />
                </svg>
              </SocialIcon>


              <SocialIcon
                href="https://x.com/SouthTravels"
                label="X"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="h-4 w-4"
                >
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.657l-5.214-6.817-5.965 6.817H1.682l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77Z" />
                </svg>
              </SocialIcon>


              <SocialIcon
                href="https://www.linkedin.com/company/southtravelsuae/"
                label="LinkedIn"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="h-4 w-4"
                >
                  <path d="M5.04 3.5a2.25 2.25 0 1 0 0 4.5 2.25 2.25 0 0 0 0-4.5ZM3 9h4v12H3V9Zm6 0h3.83v1.64h.05c.53-1 1.83-2.06 3.77-2.06 4.03 0 4.77 2.65 4.77 6.1V21h-4v-5.6c0-1.34-.02-3.06-1.87-3.06-1.87 0-2.16 1.46-2.16 2.96V21H9V9Z" />
                </svg>
              </SocialIcon>

            </div>


            {/* TRAVEL MESSAGE */}
            <div className="mt-5">

              <p className="text-sm font-medium italic text-white/75">
                Your Dubai Journey
              </p>

              <p className="text-sm font-medium italic text-white/75">
                Starts Here ✈
              </p>

              {/* Flight path */}
              <div className="relative mt-2 h-5 w-40">

                <div
                  className="
                    absolute
                    left-0
                    top-2
                    h-4
                    w-32
                    rounded-[50%]
                    border-b-2
                    border-dashed
                    border-[#F7B928]
                    rotate-[-5deg]
                  "
                />

                <span className="absolute right-0 top-0 text-[#F7B928]">
                  ✈
                </span>

              </div>

            </div>

          </div>

        </div>


        {/* =====================================================
            BOTTOM BAR
        ====================================================== */}

        <div className="border-t border-[#F7B928]/70">

          <div
            className="
              flex
              flex-col
              items-center
              justify-between
              gap-2
              py-4
              text-center
              sm:flex-row
              sm:text-left
            "
          >

            <p className="text-[10px] text-white/45 sm:text-[11px]">
              © {currentYear} Cheap Dubai Hotels. All rights reserved.
            </p>


            <div
              className="
                flex
                items-center
                gap-3
                text-[10px]
                text-white/45
                sm:text-[11px]
              "
            >

              <Link
                to="/privacy-policy"
                className="transition-colors hover:text-[#F7B928]"
              >
                Privacy Policy
              </Link>

              <span className="text-[#F7B928]">
                |
              </span>

              <Link
                to="/terms-conditions"
                className="transition-colors hover:text-[#F7B928]"
              >
                Terms & Conditions
              </Link>

            </div>

          </div>

        </div>

      </div>

    </footer>
  );
}


/* =============================================================
   FOOTER TITLE
============================================================= */

function FooterTitle({ title }) {
  return (
    <>
      <h3
        className="
          text-sm
          font-bold
          text-white
        "
      >
        {title}
      </h3>

      <div className="mt-2 h-[2px] w-7 bg-[#F7B928]" />
    </>
  );
}


/* =============================================================
   FOOTER LINK
============================================================= */

function FooterLink({ to, children }) {
  return (
    <Link
      to={to}
      className="
        group
        flex
        items-center
        gap-2
        text-xs
        text-white/60
        transition-all
        duration-200
        hover:translate-x-1
        hover:text-white
      "
    >
      <span className="text-[#F7B928]">
        ›
      </span>

      {children}
    </Link>
  );
}


/* =============================================================
   SOCIAL ICON
============================================================= */

function SocialIcon({ href, label, children }) {
  return (
    <a
      href={href}
      aria-label={label}
      target="_blank"
      rel="noopener noreferrer"
      className="
        flex
        h-9
        w-9
        items-center
        justify-center
        rounded-full
        border
        border-white/20
        bg-[#0B6E8E]/30
        text-white/75
        transition-all
        duration-200
        hover:-translate-y-1
        hover:border-[#F7B928]
        hover:bg-[#F7B928]
        hover:text-[#062F4F]
      "
    >
      {children}
    </a>
  );
}